import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ModalCloseButton } from './icons/AdminUiIcons';

/**
 * Generic confirmation dialog — replaces window.confirm() throughout the app.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm('Are you sure?');
 *
 *   // With full options:
 *   const ok = await confirm({
 *     title: 'Cancel order',
 *     message: 'Do you want to request cancellation for this order?',
 *     confirmLabel: 'Yes, cancel',
 *     cancelLabel: 'Keep order',
 *     variant: 'danger',   // 'danger' | 'primary' | 'warning'  (default: 'primary')
 *   });
 */

const ConfirmDialogContext = createContext(null);

const DEFAULT_OPTIONS = {
  title: 'Are you sure?',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  variant: 'primary',
};

/**
 * Must sit above every app modal overlay (ops 10040, jewelry 99999, image lightbox 10040, etc.).
 * Parents keep listening for Escape while open — capture-phase handler cancels confirm first.
 */
const CONFIRM_Z_INDEX = 110000;
const CONFIRM_BACKDROP_Z_INDEX = 109999;

function ConfirmDialogModal({ open, options, onConfirm, onCancel }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      onCancel();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onCancel]);

  if (!open) return null;

  const { title, message, confirmLabel, cancelLabel, variant } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  const btnClass =
    variant === 'danger'
      ? 'btn-danger'
      : variant === 'warning'
        ? 'btn-warning'
        : 'btn-primary';

  return createPortal(
    <>
      <div
        className="modal fade show d-block confirm-dialog-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        tabIndex={-1}
        style={{ zIndex: CONFIRM_Z_INDEX }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content shadow">
            <div className="modal-header border-0 pb-1">
              <h2 className="modal-title h6 fw-semibold" id="confirm-dialog-title">
                {title}
              </h2>
              <ModalCloseButton onClick={onCancel} />
            </div>
            {message ? (
              <div className="modal-body py-2">
                <p className="mb-0 small">{message}</p>
              </div>
            ) : null}
            <div className="modal-footer border-0 pt-1 gap-2">
              {cancelLabel ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm rounded-pill"
                  onClick={onCancel}
                >
                  {cancelLabel}
                </button>
              ) : null}
              <button
                type="button"
                className={`btn ${btnClass} btn-sm rounded-pill`}
                onClick={onConfirm}
                autoFocus
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show confirm-dialog-backdrop"
        style={{ zIndex: CONFIRM_BACKDROP_Z_INDEX }}
      />
    </>,
    document.body
  );
}

export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState({ open: false, options: {} });
  const resolveRef = useRef(null);

  useEffect(() => {
    if (!state.open) return undefined;
    const scrollY = window.scrollY;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;
    document.body.classList.add('modal-open');
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, [state.open]);

  const confirm = useCallback((optionsOrMessage) => {
    const options =
      typeof optionsOrMessage === 'string'
        ? { message: optionsOrMessage }
        : optionsOrMessage || {};

    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, options });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setState({ open: false, options: {} });
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setState({ open: false, options: {} });
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      <ConfirmDialogModal
        open={state.open}
        options={state.options}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmDialogContext.Provider>
  );
}

/** Returns an async confirm() function. Must be inside <ConfirmDialogProvider>. */
export function useConfirm() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmDialogProvider');
  return ctx;
}
